import React from 'react';
import { useForm } from '../../context/FormContext';
import StepLayout from '../StepLayout';
import SelectionCard from '../SelectionCard';

function Step1BusinessDetails() {
  const { formData, updateFormData } = useForm();
  const stepData = formData.step1;

  const handleInputChange = (field, value) => {
    updateFormData('step1', { [field]: value });
  };

  const industries = [
    { value: 'Beauty-hair-wellness', label: '💅 Beauty, Hair & Wellness' },
    { value: 'Construction-home-improvement', label: '🏗️ Construction & Home Improvement' },
    { value: 'Consulting-professional-services', label: '💼 Consulting & Professional Services' },
    { value: 'Creative-media', label: '🎨 Creative & Media (Design, Photography, Marketing, Printing)' },
    { value: 'Education-training', label: '📚 Education & Training' },
    { value: 'Events-entertainment-hospitality', label: '🎉 Events, Entertainment & Hospitality' },
    { value: 'Farming-agriculture', label: '🌾 Farming & Agriculture' },
    { value: 'Finance-legal', label: '⚖️ Finance & Legal Services' },
    { value: 'Healthcare-medical', label: '🏥 Healthcare & Medical Practices' },
    { value: 'Landscaping-gardening', label: '🌳 Landscaping & Gardening' },
    { value: 'Laundry-dry-cleaning', label: '👔 Laundry & Dry-Cleaning' },
    { value: 'Manufacturing-industrial', label: '🏭 Manufacturing & Industrial' },
    { value: 'Non-profit-community', label: '🤝 Non-Profit & Community Services' },
    { value: 'Real-estate-property', label: '🏘️ Real Estate & Property Management' },
    { value: 'Restaurant-catering-food', label: '🍽️ Restaurant, Catering & Food Services' },
    { value: 'Retail-ecommerce', label: '🛒 Retail & E-commerce' },
    { value: 'Technology-it', label: '💻 Technology & IT Services' },
    { value: 'Transport-logistics', label: '🚚 Transport & Logistics' },
    { value: 'Tourism-travel-accommodation', label: '✈️ Tourism, Travel & Accommodation' },
    { value: 'Other', label: '📋 Other' }
  ];

  const websiteTypeOptions = [
    {
      id: 'refresh',
      title: '✨ Refresh my existing site',
      description: 'Update and improve your current website'
    },
    {
      id: 'new',
      title: '🆕 Design a new website',
      description: 'Create a brand new website from scratch'
    }
  ];

  return (
    <StepLayout
      title="Welcome! Let's create something amazing together 🚀"
      stepNumber={1}
      totalSteps={7}
      nextButtonText="Let's get creative"
      nextStep={2}
    >
      <div className="p-4 bg-[#F9FAFB] border-l-4 border-[#FF5841] rounded-lg mb-8">
        <p className="text-gray-700 leading-relaxed">
          You are just a few steps away from having a website that perfectly captures your brand's identity. This short, seven-step journey is designed to be simple, inspiring, and enjoyable with no tech jargon or stress. Your progress is automatically saved, so you can return at any time to complete it.
        </p>
        <p className="text-gray-700 mt-3">
          💡 Prefer to share your ideas or business profile instead? You can email your content directly to <a href="mailto:design@primehost.co.za" className="text-[#FF5841] hover:underline font-medium">design@primehost.co.za</a> and we will take it from there.
        </p>
      </div>

      <div className="space-y-6">
        {/* Business Name */}
        <div>
          <label className="block text-base font-medium text-gray-900 mb-2">
            Business name <span className="mandatory-asterisk">*</span>
          </label>
          <input
            type="text"
            value={stepData.businessName}
            onChange={(e) => handleInputChange('businessName', e.target.value)}
            placeholder="Enter your business name"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent transition-colors duration-200 text-base"
          />
        </div>

        {/* Contact Person Name */}
        <div>
          <label className="block text-base font-medium text-gray-900 mb-2">
            Contact person name <span className="mandatory-asterisk">*</span>
          </label>
          <input
            type="text"
            value={stepData.contactName}
            onChange={(e) => handleInputChange('contactName', e.target.value)}
            placeholder="Enter the main contact person's name"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent transition-colors duration-200 text-base"
          />
        </div>

        {/* Email Address */}
        <div>
          <label className="block text-base font-medium text-gray-900 mb-2">
            Email address <span className="mandatory-asterisk">*</span>
          </label>
          <input
            type="email"
            value={stepData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="your.email@company.com"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent transition-colors duration-200 text-base"
          />
        </div>

        {/* Phone Number */}
        <div>
          <label className="block text-base font-medium text-gray-900 mb-2">
            Phone number
          </label>
          <input
            type="tel"
            value={stepData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="+27 10 123 4567"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent transition-colors duration-200 text-base"
          />
        </div>

        {/* Industry */}
        <div>
          <label className="block text-base font-medium text-gray-900 mb-2">
            Industry
          </label>
          <select
            value={stepData.industry}
            onChange={(e) => handleInputChange('industry', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent transition-colors duration-200 text-base"
          >
            <option value="">Select your industry</option>
            {industries.map((industry) => (
              <option key={industry.value} value={industry.value}>
                {industry.label}
              </option>
            ))}
          </select>
        </div>

        {/* Business Description */}
        <div>
          <label className="block text-base font-medium text-gray-900 mb-2">
            Business description
          </label>
          <textarea
            value={stepData.businessDescription}
            onChange={(e) => handleInputChange('businessDescription', e.target.value)}
            placeholder="Briefly describe what your business does and what makes it unique..."
            rows={4}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent transition-colors duration-200 resize-none text-base"
          />
          <p className="text-sm text-gray-500 mt-1">
            {stepData.businessDescription.length}/500 characters
          </p>
        </div>

        {/* Website Type */}
        <div>
          <label className="block text-base font-medium text-gray-900 mb-4">
            Would you like us to revamp your existing website or create a brand new one?
          </label>
          <div className="grid grid-cols-1 gap-4 mb-4">
            {websiteTypeOptions.map((option) => (
              <SelectionCard
                key={option.id}
                title={option.title}
                description={option.description}
                selected={stepData.websiteType === option.id}
                onClick={() => handleInputChange('websiteType', option.id)}
              />
            ))}
          </div>

          {/* Conditional Fields */}
          {stepData.websiteType === 'refresh' && (
            <div className="space-y-4 mt-6">
              <div>
                <label className="block text-base font-medium text-gray-900 mb-2">
                  Current website URL
                </label>
                <input
                  type="url"
                  value={stepData.currentWebsiteUrl}
                  onChange={(e) => handleInputChange('currentWebsiteUrl', e.target.value)}
                  placeholder="https://yourwebsite.co.za"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent transition-colors duration-200 text-base"
                />
              </div>
              <div>
                <label className="block text-base font-medium text-gray-900 mb-2">
                  What would you like to change or improve?
                </label>
                <textarea
                  value={stepData.improvementNeeds}
                  onChange={(e) => handleInputChange('improvementNeeds', e.target.value)}
                  placeholder="Describe what aspects of your current website you would like to improve..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF5841] focus:border-transparent transition-colors duration-200 resize-none text-base"
                />
              </div>
            </div>
          )}

          {stepData.websiteType === 'new' && (
            <div className="mt-6 p-4 bg-[#E5F9F9] border-l-4 border-[#008080] rounded-lg">
              <p className="text-[#008080] font-400">
                <b>✓ Perfect!</b> We will build your website from scratch.
              </p>
            </div>
          )}
        </div>
      </div>
    </StepLayout>
  );
}

export default Step1BusinessDetails;